const isForegroundActive = () => {
  if (typeof document === "undefined") return true;
  if (document.visibilityState === "hidden") return false;
  if (typeof document.hasFocus === "function" && !document.hasFocus()) return false;
  return true;
};

export const createActiveDurationTracker = () => {
  let elapsedMs = 0;
  let activeStartedAt = null;
  let isTracking = false;
  let cleanupListeners = null;

  const pauseForInactive = (now = Date.now()) => {
    if (activeStartedAt === null) return;
    elapsedMs += Math.max(0, now - activeStartedAt);
    activeStartedAt = null;
  };

  const syncActiveState = () => {
    if (!isTracking) return;
    if (isForegroundActive()) {
      if (activeStartedAt === null) {
        activeStartedAt = Date.now();
      }
    } else {
      pauseForInactive();
    }
  };

  const ensureListeners = () => {
    if (cleanupListeners || typeof window === "undefined" || typeof document === "undefined") return;

    const handleInactive = () => pauseForInactive();
    document.addEventListener("visibilitychange", syncActiveState);
    window.addEventListener("focus", syncActiveState);
    window.addEventListener("blur", handleInactive);
    window.addEventListener("pagehide", handleInactive);

    cleanupListeners = () => {
      document.removeEventListener("visibilitychange", syncActiveState);
      window.removeEventListener("focus", syncActiveState);
      window.removeEventListener("blur", handleInactive);
      window.removeEventListener("pagehide", handleInactive);
      cleanupListeners = null;
    };
  };

  const resume = (now = Date.now()) => {
    if (isTracking) return;
    isTracking = true;
    ensureListeners();
    if (isForegroundActive()) {
      activeStartedAt = now;
    }
  };

  const pause = (now = Date.now()) => {
    if (!isTracking) return;
    pauseForInactive(now);
    isTracking = false;
    cleanupListeners?.();
  };

  const getElapsed = (now = Date.now()) => {
    if (!isTracking || activeStartedAt === null) return Math.round(elapsedMs);
    return Math.round(elapsedMs + Math.max(0, now - activeStartedAt));
  };

  const reset = () => {
    elapsedMs = 0;
    activeStartedAt = null;
    isTracking = false;
    cleanupListeners?.();
  };

  const stop = (now = Date.now()) => {
    const total = getElapsed(now);
    reset();
    return total;
  };

  return {
    start: (now = Date.now()) => {
      reset();
      resume(now);
    },
    resume,
    pause,
    stop,
    reset,
    getElapsed,
  };
};
